import { InspectionListItem, InspectionStatus, getLocalDateString } from '../../../lib/inspections';
import { getEvidenceDisplayName } from '@safetyhub/shared';

// 날짜별 점검 집계 (담당자 포함)
/** 셀 이벤트 pill 1개 = 점검 1건 (목업 `.ev` — 담당자 이름을 상태색 pill로) */
export interface DayEventBadge {
  name: string;
  status: InspectionStatus;
}

export interface StatusCounts {
  scheduled: number;
  inProgress: number;
  completed: number;
}

export interface DaySummary {
  total: number;
  hasPendingRequest: boolean;
  /** 점검 1건당 1개, STATUS_ORDER 정렬(진행중 먼저) — 셀은 앞에서 자르기만 한다 */
  events: DayEventBadge[];
  statusCounts: StatusCounts;
}

// 이벤트 pill의 상태별 배경+글자색 — 목업 `.ev`(연배경 100 + 진한 글자 700, 점은
// currentColor). `CalendarLegend` 스와치(400)는 이 두 명도를 한 색으로 대변한다(시안 페어링) —
// 상태→색 대응을 한쪽만 바꾸면 범례가 조용히 거짓말을 한다.
export const EVENT_PILL_CLASS: Record<string, string> = {
  [InspectionStatus.SCHEDULED]: 'bg-violet-100 text-violet-700',
  [InspectionStatus.IN_PROGRESS]: 'bg-blue-100 text-blue-700',
  [InspectionStatus.COMPLETED]: 'bg-emerald-100 text-emerald-700',
};

// 상태 표시 순서: 진행중 → 예약 → 완료 (셀 안에서 눈에 잘 띄는 순)
export const STATUS_ORDER: Record<string, number> = {
  [InspectionStatus.IN_PROGRESS]: 0,
  [InspectionStatus.SCHEDULED]: 1,
  [InspectionStatus.COMPLETED]: 2,
};

/** 점검 목록 → 날짜별 집계 맵 (월간/주간 달력 공용) */
export function buildDaySummaryMap(inspections: InspectionListItem[]): Map<string, DaySummary> {
  const map = new Map<string, DaySummary>();
  for (const insp of inspections) {
    const rawDate = insp.scheduledAt ?? insp.createdAt;
    const dateKey = getLocalDateString(new Date(rawDate));
    const cur = map.get(dateKey) ?? {
      total: 0,
      hasPendingRequest: false,
      events: [],
      statusCounts: { scheduled: 0, inProgress: 0, completed: 0 },
    };
    cur.total++;
    if (insp.hasPendingScheduleChangeRequest) cur.hasPendingRequest = true;
    if (insp.status === InspectionStatus.SCHEDULED) cur.statusCounts.scheduled++;
    else if (insp.status === InspectionStatus.IN_PROGRESS) cur.statusCounts.inProgress++;
    else if (insp.status === InspectionStatus.COMPLETED) cur.statusCounts.completed++;
    // 담당자별 그룹핑: PRIMARY collaborator 스냅샷 우선, 없으면 inspector 라이브 fallback
    const activeCollabs = (insp.collaborators ?? []).filter((c) => !c.leftAt && !c.excludedAt);
    const primaryCollab = activeCollabs.find((c) => c.role === 'PRIMARY') ?? activeCollabs[0] ?? null;
    const inspName = getEvidenceDisplayName(
      primaryCollab?.signerName ?? primaryCollab?.joinSnapshotName ?? null,
      { name: insp.inspector?.member?.name, deletedAt: insp.inspector?.deletedAt },
      '-',
    );
    cur.events.push({ name: inspName, status: insp.status as InspectionStatus });
    map.set(dateKey, cur);
  }
  // 상태 우선순위 정렬(진행중 → 예약 → 완료, 그 외 뒤) — 같은 상태끼리는 입력 순서 유지(stable)
  for (const summary of map.values()) {
    summary.events.sort(
      (a, b) => (STATUS_ORDER[a.status] ?? 3) - (STATUS_ORDER[b.status] ?? 3),
    );
  }
  return map;
}
